// App root — the ONLY place that decides which UI shell renders.
//
//   src/core        shared logic (API, MQTT, state, device maps) — written once
//   src/ui/mobile   Flutter-replica phone UI (Capacitor Android/iOS, narrow web)
//   src/ui/desktop  wide sidebar UI          (desktop browser)
//
// Both shells sit inside the same provider, theme and router, so a session, an
// MQTT connection and a device command behave identically on either.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HashRouter, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { App as CapacitorApp } from '@capacitor/app';
import { AppProvider, useApp } from './core/state/AppContext';
import { buildTheme } from './core/theme/buildTheme';
import { connectMqtt } from './core/mqtt/mqttClient';
import { useUiMode } from './core/platform';
import { initOfflineWatch } from './core/offline/offlineMode';
import { parentOf, homeBack } from './core/nav/appNav';
import { runBackIntercept } from './core/nav/backIntercept';

import DesktopRoutes from './ui/desktop/DesktopRoutes';
import MobileRoutes from './ui/mobile/MobileRoutes';
import OtaUpdater from './ui/shared/OtaUpdater';
import ExitConfirmDialog from './ui/shared/ExitConfirmDialog';
import ForceUpgradeGate from './ui/shared/ForceUpgradeGate';
import EscapeBack from './ui/shared/EscapeBack';
import ToastHost from './ui/shared/toast';

// Android hardware back: navigate within the SPA instead of letting the webview
// exit the app. (No-op on web — there is no hardware back button.)
//
// Mirrors the Flutter app's WillPopScope chain:
//   • not at a root screen        -> go UP one level of the structure
//   • on /home but not the first tab -> return to the Favourites tab
//     (Flutter's non-first tabs pop back to HomeScreen rather than exiting)
//   • at a root screen            -> ASK before exiting (Flutter's exitPopup)
//
// Asking matters: without it a single stray tap on the home screen kills the app
// mid-session, which is easy to do one-handed.
//
// ⚠️ THE STRUCTURE, NOT `navigate(-1)`. This is the back button people actually
// press on a phone, and it used to pop browser history while the on-screen
// arrow walked the structure — two back buttons with two different answers,
// agreeing only when the user had arrived the long way round. They never have
// after an auto-drill, which REPLACES the levels it skips, so history there is a
// single entry and popping it exits the app. `parentOf` is now the one answer
// both of them use. See core/nav/appNav.js.
const ROOT_PATHS = ['/home', '/login', '/splash', '/blocked'];

function HardwareBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const [askExit, setAskExit] = useState(false);

  // ⚠️ SUBSCRIBED ONCE, AND THE HANDLER LIVES IN A REF. `addListener` is async
  // while an effect's cleanup is synchronous, so re-subscribing on every
  // navigation could run the cleanup before the handle existed — leaving the
  // old listener attached for ever. They then stacked up, one per screen
  // visited, and a single press fired all of them: several `navigate` calls at
  // once, which SKIPS levels. That is what "back jumps straight to the rooms
  // list" was.
  const onBack = useRef(() => {});
  onBack.current = () => {
    // A screen stepping back INSIDE itself first (core/nav/backIntercept).
    if (runBackIntercept()) return;
    const target = parentOf(location.pathname, location.state);
    if (target) { navigate(target.path, { state: target.state }); return; }

    // No structural parent — a deep link, or a screen outside the
    // location/room/device spine. History is then the only honest answer.
    if (!ROOT_PATHS.includes(location.pathname)) { navigate(-1); return; }

    // On the tabbed home screen the rule is `homeBack` — a ROOT tab asks to
    // exit, anything else steps back to Favourites first. Tabs are written with
    // `replace`, so back cannot unwind them and the rule cannot live in history.
    if (location.pathname === '/home') {
      const { exit, tab } = homeBack(new URLSearchParams(location.search).get('tab'));
      if (!exit) { navigate(`/home?tab=${tab}`, { replace: true }); return; }
    }

    setAskExit(true);
  };

  useEffect(() => {
    let handle = null;
    let cancelled = false;
    CapacitorApp.addListener('backButton', () => onBack.current())
      .then((h) => {
        handle = h;
        // Unmounted while the subscription was still resolving.
        if (cancelled) { try { h.remove(); } catch (e) { /* ignore */ } }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { handle && handle.remove(); } catch (e) { /* ignore */ }
    };
  }, []);

  return (
    <ExitConfirmDialog
      open={askExit}
      onCancel={() => setAskExit(false)}
      onExit={() => {
        setAskExit(false);
        CapacitorApp.exitApp().catch(() => {});
      }}
    />
  );
}

function Shell() {
  const { user, isAuthed } = useApp();
  const uiMode = useUiMode();
  // The desktop is dense (smaller font, tighter spacing); the phone keeps its touch sizes.
  const theme = useMemo(() => buildTheme(user, { dense: uiMode !== 'mobile' }), [user, uiMode]);

  // Watch connectivity from the very start: everything downstream (status
  // badges, control transport, the "no network" hint) reads offlineMode, and it
  // must be right before the first screen asks.
  useEffect(() => { initOfflineWatch(); }, []);

  // Connect MQTT once the user is logged in so live control/status work.
  useEffect(() => {
    if (isAuthed) connectMqtt();
  }, [isAuthed]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Self-hosted OTA live updates. Mounted once, outside the router, so a
          navigation can't remount it and fire a second update check. */}
      {/* TWO update paths, checked on every start:
         *
         *   1. PLAY STORE  (ForceUpgradeGate) — a newer NATIVE build. Blocking:
         *      the app cannot be used until it is installed, because a native
         *      release is the only way to ship plugin/permission/SDK changes.
         *   2. OTA         (OtaUpdater) — a newer WEB BUNDLE. Silent: downloads
         *      in the background and applies on the next cold start.
         *
         * ⚠️ OtaUpdater IS A SIBLING OF THE GATE, NOT A CHILD — it used to be a
         * child, and that is a trap with two teeth:
         *
         *   • `ForceUpgradeGate` renders the blocking screen INSTEAD of its
         *     children whenever the scraped Play version is higher. So the first
         *     Play release above the installed base would stop OtaUpdater from
         *     mounting AT ALL on exactly the phones an OTA exists to rescue —
         *     closing the live-update channel permanently, on the oldest
         *     installs, at the moment it is most needed.
         *   • `notifyAppReady()` is what cancels the plugin's rollback watchdog.
         *     Behind the gate it sat downstream of a play.google.com SCRAPE with
         *     a 5 s connect + 5 s read timeout — a network call racing a ~10 s
         *     watchdog on the first boot of every new bundle. Losing that race
         *     silently reverts and DELETES the update that just applied.
         *
         * The original reason for nesting was that a bundle built against native
         * code this install lacks would break it. `minNative` in the manifest is
         * the guard for that now (core/ota/otaClient.js), and it is a precise one
         * — so the nesting bought nothing and cost the channel. OtaUpdater
         * renders null, so nothing about the gate's UI changes. */}
      <OtaUpdater />
      <ToastHost />
      <ForceUpgradeGate>
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <HardwareBack />
          {/* Escape = back on every screen. Desktop has no hardware back key,
              so without this a full-window screen can only be left via its own
              arrow. */}
          <EscapeBack />
          {uiMode === 'mobile' ? <MobileRoutes /> : <DesktopRoutes />}
        </HashRouter>
      </ForceUpgradeGate>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
